import {
  type AmplifyError,
  confirmSignIn,
} from '@backoffice-frontend/authentication'
import { FormTextfield } from '@backoffice-frontend/forms'
import {
  MoiaGrid,
  useMoiaErrorNotification,
} from '@backoffice-frontend/patterns'
import { Button, LoadingIndicator } from '@moia-dev/pace-web'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type MfaCodeType = {
  mfaCode: string
}

export const MultiFactorAuthenticationForm = ({
  isMobile,
}: {
  isMobile: boolean
}) => {
  const [submitting, setSubmitting] = useState(false)
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { t } = useTranslation()

  const { handleSubmit, control } = useForm<MfaCodeType>({
    mode: 'onChange',
    defaultValues: {
      mfaCode: '',
    },
  })

  const handleMfaSubmit = async (data: MfaCodeType) => {
    setSubmitting(true)
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: data.mfaCode,
      })
      if (!isSignedIn) {
        throw new Error('User is not signed in after confirming MFA code')
      }
    } catch (_error) {
      const error = _error as AmplifyError
      if (error.name === 'VerifySoftwareTokenException') {
        enqueueMoiaErrorNotification(t('loginView_mfaCodeInvalid'))
      } else {
        enqueueMoiaErrorNotification(error.message)
      }
    } finally {
      setSubmitting(false)
    }
  }
  return (
    <form onSubmit={handleSubmit(handleMfaSubmit)}>
      <div>
        <FormTextfield
          control={control}
          autoFocus
          caption={t('loginView_mfaCode_caption')}
          name="mfaCode"
          label={t('loginView_mfaCode_label')}
          size={isMobile ? 'compact' : 'default'}
        />
        <MoiaGrid
          alignItems="center"
          gridTemplateColumns="1fr min-content"
          justifyContent="flex-end"
          mt={4}
        >
          <p>{t('loginView_mfaCode_hint')} </p>
          <Button
            variant="primary"
            icon={submitting ? <LoadingIndicator /> : undefined}
            type="submit"
            size="compact"
            label={t('loginView_login_button')}
          />
        </MoiaGrid>
      </div>
    </form>
  )
}
